import { lazy, Suspense, useCallback, useMemo, useState } from "react";

import * as Sentry from "@sentry/react";
import {
  Button,
  Checkbox,
  Divider,
  Alert,
  Skeleton,
  TextArea,
  TextField,
} from "@stacklet/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  ConnectionHandler,
  graphql,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { useNavigate } from "react-router";

import { ErrorAlerts } from "app/components/ErrorAlerts";
import { HelpDrawerContent } from "app/components/HelpDrawer/HelpDrawerContent";
import { paths } from "app/constants/paths";
import { useAlertContext } from "app/contexts";
import { useFormErrors } from "app/hooks/forms/useFormErrors";
import { AddNotificationRuleMutation } from "app/mutations/AddNotificationRuleMutation";
import { UpdateNotificationRuleMutation } from "app/mutations/UpdateNotificationRuleMutation";
import { invalidateConnections } from "app/utils/relay";
import { RelayEnvironment } from "RelayEnvironment";

import { RULES_DELIVERY_METHOD_HELP_CONTENT } from "../consts";
import { useNotificationRuleFormContext } from "../contexts/NotificationRuleFormContext";
import { useNotificationRulePreview } from "../hooks/useNotificationRulePreview";
import {
  formatNotificationRuleInput,
  formatUpdateNotificationRuleInput,
} from "../utils/formatNotificationRuleInput";
import { getJiraInstanceSignature } from "../utils/jiraInstanceSignature";
import { JiraAssignee } from "./dropdowns/JiraAssignee";
import { JiraGrouping } from "./dropdowns/JiraGrouping";
import { JiraInstance } from "./dropdowns/JiraInstance";
import { JiraSpace } from "./dropdowns/JiraSpace";
import { JiraWorkType } from "./dropdowns/JiraWorkType";
import { HighIssueCountConfirmationDialog } from "./HighIssueCountConfirmationDialog";
import { SelectedRulesFiltersDisclosure } from "./SelectedRulesFiltersDisclosure";

import type { RulesDeliveryMethodQuery as RulesDeliveryMethodQueryType } from "./__generated__/RulesDeliveryMethodQuery.graphql";
import type { JiraInstanceData } from "./dropdowns/JiraInstance";
import type { RulesFilterFormData } from "./schema";
import type { AddNotificationRuleMutation as AddNotificationRuleMutationType } from "app/mutations/__generated__/AddNotificationRuleMutation.graphql";
import type { UpdateNotificationRuleMutation as UpdateNotificationRuleMutationType } from "app/mutations/__generated__/UpdateNotificationRuleMutation.graphql";
import type { RecordSourceSelectorProxy } from "relay-runtime";

const NotificationRulePreview = lazy(() =>
  import("./NotificationRulePreview").then((m) => ({
    default: m.NotificationRulePreview,
  })),
);

interface Props {
  onCancel?: () => void;
}

export function RulesDeliveryMethod({ onCancel }: Props) {
  const navigate = useNavigate();
  const { alertDispatch } = useAlertContext();
  const { control, getValues, handleSubmit, setValue } =
    useFormContext<RulesFilterFormData>();
  const [formErrors, setFormErrors] = useFormErrors();
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<RulesFilterFormData | null>(null);
  const isHighIssueCountDialogOpen = pendingSubmissionData !== null;
  const { isEditMode, ruleUUID } = useNotificationRuleFormContext();
  const {
    hasFilters,
    result: previewResult,
    isLoading: isPreviewLoading,
    isRefreshing: isPreviewRefreshing,
    fetchError: previewFetchError,
    issueCount,
  } = useNotificationRulePreview();
  // Fetch Jira instances, tag keys, and project data
  const data = useLazyLoadQuery<RulesDeliveryMethodQueryType>(
    graphql`
      query RulesDeliveryMethodQuery {
        ...JiraAssignee_accountTagKeys
        ...JiraAssignee_accountTagValues
        ...JiraAssignee_resourceTagKeys
        ...JiraAssignee_whoami
        ...JiraGrouping_tagKeys
        ...JiraInstance_jiraInstances
        project(name: "default") {
          id
          uuid
        }
      }
    `,
    {},
  );
  const [loadedInstanceMap, setLoadedInstanceMap] = useState<
    Map<string, JiraInstanceData>
  >(new Map());
  const providerFilters = useWatch<RulesFilterFormData, "providerFilters">({
    control,
    name: "providerFilters",
    defaultValue: [],
  });
  const accountFilters = useWatch<RulesFilterFormData, "accountFilters">({
    control,
    name: "accountFilters",
  });
  const resourceFilters = useWatch<RulesFilterFormData, "resourceFilters">({
    control,
    name: "resourceFilters",
  });
  const policyFilters = useWatch<RulesFilterFormData, "policyFilters">({
    control,
    name: "policyFilters",
  });
  const bindingFilters = useWatch<RulesFilterFormData, "bindingFilters">({
    control,
    name: "bindingFilters",
  });
  const initialJiraInstance = getValues("jiraInstance");
  const initialJiraSpace = getValues("jiraSpace");
  const jiraInstance = useWatch<RulesFilterFormData, "jiraInstance">({
    control,
    name: "jiraInstance",
    defaultValue: initialJiraInstance,
  });
  const jiraSpace = useWatch<RulesFilterFormData, "jiraSpace">({
    control,
    name: "jiraSpace",
    defaultValue: initialJiraSpace,
  });
  const selectedInstanceFromQuery = useMemo(() => {
    const selectedUuid = jiraInstance?.uuid;
    if (!selectedUuid) return null;

    return loadedInstanceMap.get(selectedUuid) ?? null;
  }, [jiraInstance?.uuid, loadedInstanceMap]);

  const selectedInstance = selectedInstanceFromQuery ?? jiraInstance ?? null;

  // Extract spaces and default space from selected instance
  const availableSpaces = selectedInstance?.spaces ?? [];
  const defaultSpace = selectedInstance?.defaultSpace ?? null;

  // Get the selected space key from the selections array (last item)
  const selectedSpaceKey = jiraSpace?.[jiraSpace.length - 1];

  // Get work types from the selected space
  const selectedSpace = availableSpaces.find(
    (space) => space.key === selectedSpaceKey,
  );
  const availableWorkTypes = selectedSpace?.workTypes ?? [];

  const isWorkTypeDisabled = !selectedInstance || !selectedSpaceKey;

  const projectUUID = data?.project?.uuid;
  const projectId = data?.project?.id;

  const [addNotificationRule, isInFlight] =
    useMutation<AddNotificationRuleMutationType>(AddNotificationRuleMutation);

  const [updateNotificationRule, isInFlightUpdate] =
    useMutation<UpdateNotificationRuleMutationType>(
      UpdateNotificationRuleMutation,
    );

  const connectionID = projectId
    ? ConnectionHandler.getConnectionID(
        projectId,
        "NotificationRulesList_notificationRules",
      )
    : null;

  const onSubmit = (formData: RulesFilterFormData) => {
    if (!projectUUID) return;

    const input = formatNotificationRuleInput(
      formData,
      projectUUID,
      formData.name,
    );

    addNotificationRule({
      variables: {
        input,
        connections: connectionID != null ? [connectionID] : [],
      },
      onCompleted: (response, errors) => {
        setPendingSubmissionData(null);
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
          return;
        }

        if (response.addNotificationRule?.problems?.length) {
          const problemMessages = response.addNotificationRule.problems
            .map((problem) => problem.message)
            .join(", ");
          alertDispatch({
            message: problemMessages,
            severity: "error",
          });
          return;
        }

        if (response.addNotificationRule?.notificationRule) {
          alertDispatch({
            message: `Added notification rule ${response.addNotificationRule.notificationRule.name}`,
            severity: "success",
          });
          navigate(paths.notificationRules);
        }
      },
      onError: (error) => {
        setPendingSubmissionData(null);
        Sentry.captureException(error);
        setFormErrors([error.message]);
        alertDispatch({
          message: `Failed to create notification rule: ${error.message}`,
          severity: "error",
        });
      },
    });
  };

  const invalidateNotificationRulesList = useCallback(
    (store: RecordSourceSelectorProxy, ruleId?: string | null) => {
      if (projectId) {
        invalidateConnections(
          RelayEnvironment,
          "NotificationRulesList_notificationRules",
          projectId,
          store,
        );
      }

      if (ruleId) {
        const ruleRecord = store.get(ruleId);
        if (ruleRecord) {
          ruleRecord.invalidateRecord();
        }
      }
    },
    [projectId],
  );

  const onUpdate = (formData: RulesFilterFormData) => {
    if (!ruleUUID) return;
    const input = formatUpdateNotificationRuleInput(formData, ruleUUID);

    updateNotificationRule({
      variables: {
        input,
      },
      onCompleted: (response, errors) => {
        setPendingSubmissionData(null);
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
          return;
        }

        if (response?.updateNotificationRule?.problems?.length) {
          const problemMessages = response?.updateNotificationRule?.problems
            .map((problem: { message: string }) => problem.message)
            .join(", ");
          alertDispatch({
            message: problemMessages,
            severity: "error",
          });
          return;
        }

        if (response?.updateNotificationRule?.notificationRule) {
          alertDispatch({
            message: `Updated notification rule ${response?.updateNotificationRule?.notificationRule?.name}`,
            severity: "success",
          });
          navigate(paths.notificationRules);
        }
      },
      updater: (store) => {
        const payload = store.getRootField("updateNotificationRule");
        const notificationRule = payload?.getLinkedRecord("notificationRule");
        invalidateNotificationRulesList(store, notificationRule?.getDataID());
      },
      onError: (error) => {
        setPendingSubmissionData(null);
        Sentry.captureException(error);
        setFormErrors([error.message]);
        alertDispatch({
          message: `Failed to update notification rule: ${error.message}`,
          severity: "error",
        });
      },
    });
  };

  const isPending = isInFlight || isInFlightUpdate;
  const handleSubmitForm = isEditMode ? onUpdate : onSubmit;

  const applyJiraDefaults = useCallback(
    (instanceData: JiraInstanceData) => {
      if (isEditMode) {
        return;
      }

      const currentSpaces = getValues("jiraSpace") ?? [];
      let selectedSpaceKeyFromForm = currentSpaces[currentSpaces.length - 1];
      if (currentSpaces.length === 0) {
        const defaultSpaceKey = instanceData.defaultSpace?.key;
        const hasDefaultSpace = instanceData.spaces.some(
          (space) => space.key === defaultSpaceKey,
        );
        if (defaultSpaceKey && hasDefaultSpace) {
          selectedSpaceKeyFromForm = defaultSpaceKey;
          setValue("jiraSpace", [defaultSpaceKey], {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
      }

      const currentWorkTypes = getValues("jiraWorkType") ?? [];
      if (currentWorkTypes.length > 0) {
        return;
      }

      const isDefaultSpaceSelected =
        selectedSpaceKeyFromForm === instanceData.defaultSpace?.key;
      if (!isDefaultSpaceSelected) {
        return;
      }

      const defaultWorkTypeName = instanceData.defaultWorkType?.name;
      if (!defaultWorkTypeName) {
        return;
      }

      const selectedSpaceFromInstance = instanceData.spaces.find(
        (space) => space.key === selectedSpaceKeyFromForm,
      );
      const hasDefaultWorkType = selectedSpaceFromInstance?.workTypes.some(
        (workType) => workType.name === defaultWorkTypeName,
      );
      if (!hasDefaultWorkType) {
        return;
      }

      setValue("jiraWorkType", [defaultWorkTypeName], {
        shouldDirty: false,
        shouldTouch: false,
      });
    },
    [getValues, isEditMode, setValue],
  );

  const handleJiraInstanceChange = useCallback(
    (
      instanceData: JiraInstanceData,
      options?: { shouldDirty?: boolean; shouldTouch?: boolean },
    ) => {
      const { shouldDirty = true, shouldTouch = true } = options ?? {};

      const currentInstance = getValues("jiraInstance");
      const isSameUuid = currentInstance?.uuid === instanceData.uuid;
      const currentSignature = currentInstance
        ? getJiraInstanceSignature(currentInstance)
        : null;
      const nextSignature = getJiraInstanceSignature(instanceData);
      const hasSameData = isSameUuid && currentSignature === nextSignature;

      if (!hasSameData) {
        setValue("jiraInstance", instanceData, {
          shouldDirty,
          shouldTouch,
        });
      }

      if (!isSameUuid) {
        setValue("jiraSpace", [], { shouldDirty, shouldTouch });
        setValue("jiraWorkType", [], { shouldDirty, shouldTouch });
      }

      applyJiraDefaults(instanceData);
    },
    [applyJiraDefaults, getValues, setValue],
  );
  const handleInstancesChanged = useCallback(
    (nextMap: Map<string, JiraInstanceData>) => {
      setLoadedInstanceMap(nextMap);

      const currentInstance = getValues("jiraInstance");
      // Re-hydrate a prefilled UUID-only instance with full data once query
      // data arrives so spaces/work types are available for display. In create
      // mode this also triggers default space/work type selection.
      if (currentInstance?.uuid) {
        const hydratedCurrentInstance = nextMap.get(currentInstance.uuid);
        if (hydratedCurrentInstance) {
          handleJiraInstanceChange(hydratedCurrentInstance, {
            shouldDirty: false,
            shouldTouch: false,
          });
        }
        return;
      }

      // In create mode with no pre-selected instance, auto-select if there is
      // exactly one instance.
      if (isEditMode) {
        return;
      }

      if (nextMap.size !== 1) {
        return;
      }

      const [onlyInstance] = nextMap.values();
      if (!onlyInstance) {
        return;
      }

      setValue("jiraInstance", onlyInstance, {
        shouldDirty: false,
        shouldTouch: false,
      });
      applyJiraDefaults(onlyInstance);
    },
    [
      applyJiraDefaults,
      getValues,
      handleJiraInstanceChange,
      isEditMode,
      setValue,
    ],
  );

  const submitButtonText = isInFlight
    ? "Creating..."
    : isInFlightUpdate
      ? "Updating..."
      : isEditMode
        ? "Update Notification Rule"
        : "Create Notification Rule";

  // Treat 0 as "no limit" — the server may return 0 for instances created
  // before this feature existed. The form schema enforces positive(), so 0 is
  // never a meaningful configured value.
  const effectiveIssueLimitSoft = selectedInstance?.issueLimitSoft || null;
  const effectiveIssueLimitHard = selectedInstance?.issueLimitHard || null;
  const previewReady = !isPreviewLoading && previewResult !== null;
  const shouldBlockForIssueCount =
    previewReady &&
    effectiveIssueLimitHard !== null &&
    issueCount >= effectiveIssueLimitHard;
  const shouldWarnForIssueCount =
    previewReady &&
    !shouldBlockForIssueCount &&
    effectiveIssueLimitSoft !== null &&
    issueCount >= effectiveIssueLimitSoft;

  const handleValidFormSubmit = useCallback(
    (formData: RulesFilterFormData) => {
      if (shouldBlockForIssueCount) {
        return;
      }

      if (shouldWarnForIssueCount) {
        setPendingSubmissionData(formData);
        return;
      }

      handleSubmitForm(formData);
    },
    [handleSubmitForm, shouldBlockForIssueCount, shouldWarnForIssueCount],
  );

  const handleConfirmHighIssueCount = useCallback(() => {
    if (!pendingSubmissionData) {
      return;
    }

    handleSubmitForm(pendingSubmissionData);
  }, [handleSubmitForm, pendingSubmissionData]);

  const handleJiraSpaceChange = useCallback(
    (selections: string[]) => {
      const selectedSpaceKeyFromSelection = selections[selections.length - 1];
      const defaultWorkTypeName = selectedInstance?.defaultWorkType?.name;
      const selectedSpaceFromInstance = selectedInstance?.spaces.find(
        (space) => space.key === selectedSpaceKeyFromSelection,
      );
      const hasDefaultWorkType = selectedSpaceFromInstance?.workTypes.some(
        (workType) => workType.name === defaultWorkTypeName,
      );
      const shouldAutoSelectDefaultWorkType =
        !isEditMode &&
        selectedSpaceKeyFromSelection === selectedInstance?.defaultSpace?.key &&
        !!defaultWorkTypeName &&
        !!hasDefaultWorkType;

      // Reset work type when space changes, then auto-select default when applicable.
      setValue(
        "jiraWorkType",
        shouldAutoSelectDefaultWorkType && defaultWorkTypeName
          ? [defaultWorkTypeName]
          : [],
        shouldAutoSelectDefaultWorkType
          ? { shouldDirty: false, shouldTouch: false }
          : undefined,
      );
    },
    [isEditMode, selectedInstance, setValue],
  );

  const handleCloseHighIssueCountDialog = useCallback(() => {
    setPendingSubmissionData(null);
  }, []);

  return (
    <>
      <HelpDrawerContent content={RULES_DELIVERY_METHOD_HELP_CONTENT} />
      <form
        className="flex flex-col p-6"
        onSubmit={handleSubmit(handleValidFormSubmit, (errors) => {
          setFormErrors(
            Object.values(errors ?? {})?.map((error) => error.message ?? "") ??
              [],
          );
        })}
      >
        <SelectedRulesFiltersDisclosure
          accountFilters={accountFilters ?? []}
          bindingFilters={bindingFilters ?? []}
          policyFilters={policyFilters ?? []}
          providerFilters={providerFilters ?? []}
          resourceFilters={resourceFilters ?? []}
        />

        <Divider className="my-lg text-outline-decorative" />

        <div className="mb-lg text-label-large-strong">Create Jira Issues</div>

        <div className="mb-lg flex items-center gap-x-lg whitespace-nowrap">
          <div className="flex items-center gap-x-xs">
            In instance
            <Controller
              control={control}
              name="jiraInstance"
              render={({ field }) => (
                <JiraInstance
                  jiraInstancesRef={data}
                  onChange={handleJiraInstanceChange}
                  onInstancesChanged={handleInstancesChanged}
                  selectedInstanceUuid={field.value?.uuid}
                />
              )}
            />
          </div>
          <div className="flex items-center gap-x-xs">
            Space
            <Controller
              control={control}
              name="jiraSpace"
              render={({ field }) => (
                <JiraSpace
                  defaultSpace={defaultSpace}
                  disabled={!selectedInstance}
                  key={`${jiraInstance?.uuid ?? "no-instance"}:${availableSpaces.length}`}
                  onChange={(selections) => {
                    field.onChange(selections);
                    handleJiraSpaceChange(selections);
                  }}
                  selectedSpaceKey={field.value?.[field.value.length - 1]}
                  spaces={availableSpaces}
                />
              )}
            />
          </div>
          <div className="flex flex-1 items-center gap-x-xs">
            Work Type
            <div className="flex-1">
              <Controller
                control={control}
                name="jiraWorkType"
                render={({ field }) => (
                  <JiraWorkType
                    isDisabled={isWorkTypeDisabled}
                    isLoading={
                      Boolean(selectedSpaceKey) &&
                      Boolean(selectedInstance?.uuid) &&
                      !selectedSpace
                    }
                    key={`${selectedSpaceKey ?? "no-space"}:${availableWorkTypes.length}`}
                    onChange={field.onChange}
                    selectedWorkTypeName={field.value?.[0]}
                    workTypes={availableWorkTypes}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="mb-lg flex gap-x-lg">
          <div className="flex flex-col gap-y-xs">
            Summary
            <Controller
              control={control}
              name="jiraGrouping"
              render={({ field }) => (
                <JiraGrouping
                  onChange={field.onChange}
                  selectedGroupBy={field.value}
                  tagKeysRef={data}
                />
              )}
            />
          </div>
          <div className="flex flex-1 flex-col gap-y-xs">
            Assignee
            <Controller
              control={control}
              name="jiraAssignee"
              render={({ field }) => (
                <JiraAssignee
                  accountTagKeysRef={data}
                  accountTagValuesRef={data}
                  onChange={field.onChange}
                  resourceTagKeysRef={data}
                  selectedAssignee={field.value}
                  whoamiRef={data}
                />
              )}
            />
          </div>
        </div>
        <div className="flex flex-col gap-y-lg">
          <Controller
            control={control}
            name="issueDescription"
            render={({ field }) => (
              <div>
                <TextArea
                  assistiveText="Include a custom message in the issue description. This will be placed above the Stacklet generated issue description."
                  label="Description (Optional)"
                  textAreaProps={{
                    ...field,
                    placeholder:
                      "Enter a description for the notification rule",
                    value: field.value ?? "",
                    onChange: field.onChange,
                  }}
                />
              </div>
            )}
          />

          <Controller
            control={control}
            name="includeCostDataByDefault"
            render={({ field }) => (
              <Checkbox
                {...field}
                assistiveText="Resource matches for policies with category: Finops include cost data by default."
                isSelected={Boolean(field.value)}
                onChange={field.onChange}
                value={field.value ? "true" : "false"}
              >
                Include cost data for each resource
              </Checkbox>
            )}
          />

          <Controller
            control={control}
            name="createNewIssuesForResources"
            render={({ field }) => (
              <Checkbox
                {...field}
                assistiveText="Stacklet will only create issues for new resource matches. Existing issues will be updated when resource matches are resolved."
                isSelected={Boolean(field.value)}
                onChange={field.onChange}
                value={field.value ? "true" : "false"}
              >
                Create new issues for new resource matches once a day
              </Checkbox>
            )}
          />
        </div>

        <div className="mt-xs">
          <Suspense
            fallback={
              <Skeleton
                aria-label="Loading preview…"
                height="8rem"
                variant="rectangular"
              />
            }
          >
            <NotificationRulePreview
              fetchError={previewFetchError}
              hasFilters={hasFilters}
              isLoading={isPreviewLoading}
              isRefreshing={isPreviewRefreshing}
              issueCount={issueCount}
              result={previewResult}
            />
          </Suspense>
        </div>

        <Divider className="my-lg text-outline-decorative" />

        <Controller
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-y-xs">
              <div className="text-label-large-strong">
                Notification Rule Name
              </div>
              <TextField
                assistiveText={fieldState.error?.message}
                inputProps={{
                  ...field,
                  placeholder: "Enter a name for the rule...",
                }}
                isInvalid={!!fieldState.error}
              />
            </div>
          )}
        />
        <ErrorAlerts className="mt-3" messages={formErrors} />
        {shouldBlockForIssueCount ? (
          <Alert className="mt-3" severity="error">
            This rule would generate {issueCount} issues, which meets or exceeds
            the hard limit of {effectiveIssueLimitHard}. Reduce the scope of
            this rule before saving.
          </Alert>
        ) : null}
        {shouldWarnForIssueCount ? (
          <Alert className="mt-3" severity="warning">
            This rule will generate {issueCount} issues.
          </Alert>
        ) : null}
        <div className="mt-auto flex justify-end gap-x-2 pt-16">
          <Button onPress={onCancel} variant="tertiary">
            Cancel
          </Button>
          <Button
            isDisabled={isPending || shouldBlockForIssueCount}
            isPending={isPending}
            type="submit"
            variant="secondary"
          >
            {submitButtonText}
          </Button>
        </div>
      </form>
      <HighIssueCountConfirmationDialog
        isEditMode={isEditMode}
        isOpen={isHighIssueCountDialogOpen}
        isPending={isPending}
        issueCount={issueCount}
        onCancel={handleCloseHighIssueCountDialog}
        onConfirm={handleConfirmHighIssueCount}
      />
    </>
  );
}
